import { useCMSPageField } from '@/cms/fields'
import { useIntl } from '@/intl'
import {
  useRoomsParticipantsDistributionStore,
} from '@/scopes/common/components/Filters/Participants/useParticipantsStore'

import { useOptimizelyFlagContext } from '@/scopes/common/infrastructure/ab-testing/useOptimizely'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'

import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags'
import { ProductAdsBanner } from '@/scopes/product-ads/components/ProductAdsBanner'
import { SearchTileProductAds } from '@/scopes/product-ads/components/SearchTile'
import { useFilters, usePagination, useResults } from '@/scopes/search/search/domain/hooks'
import { SelectedFiltersTokens } from '@/scopes/search/search/pages/SearchPage/components/SelectedFiltersTokens'
import { mqMin, space, tk } from '@sunwebgroup/ui-components'
import { Banner } from '@sunwebgroup/ui-components/uds'
import { range } from 'ramda'
import { Fragment, useEffect, useRef, useState } from 'react'
import { styled } from 'styled-components'
import AccomodationTipsSlider from './AccommodationTipsSlider'
import { CollectionBanner } from './CollectionBanner'
import { CollectionBannerLegacy } from './CollectionBannerLegacy'
import { EmptyState } from './EmptyState'
import { Pagination } from './Pagination'
import { ProductCard } from './ProductCard'
import { ProductCard as ProductCardLegacy, ProductCardLoading } from './ProductCardLegacy'
import { Result } from './Result'
import { ResultsCounter } from './ResultsCounter'
import { ResultsCounterLegacy } from './ResultsCounterLegacy'
import { Sorting } from './Sorting'
import { Usps } from './Usps'

const Wrapper = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;

  padding: ${space('primitives_space_16')};
  padding-top: ${space('primitives_space_8')};

  @media ${tk('mq_tablet')} {
    ${ProductCardLegacy.modSize('tablet')} // Remove with ACTIVATE_UDS_SEARCH_PRODUCT_CARD
    ${ProductCard.modSize('tablet')}

    padding: ${space('primitives_space_24')};
    padding-top: ${space('primitives_space_16')};
  }

  @media ${tk('mq_desktop_sm')} {
    ${ProductCardLegacy.modSize('desktop')} // Remove with ACTIVATE_UDS_SEARCH_PRODUCT_CARD
    ${ProductCard.modSize('desktop')}

    padding: ${space('primitives_space_40')};
    padding-top: ${space('primitives_space_24')};
  }

  @media ${tk('mq_desktop_lg')} {
    margin-right: ${space('primitives_space_24')};
  }

  > * {
    margin-bottom: ${space('primitives_space_16')};
  }

  > *:last-child {
    margin-bottom: 0;
  }
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${space('primitives_space_8')};
`

const Content = styled.div`
  > * {
    margin-bottom: ${space('primitives_space_8')};
  }

  > *:last-child {
    margin-bottom: 0;
  }
`
// Remove with ACTIVATE_UDS_SEARCH_PRODUCT_CARD
const MobileOnlyLegacy = styled.div` 
  @media ${tk('mq_desktop_lg')} {
    display: none;
  }
`

const MobileOnlyUDS = styled.div`
  @media ${mqMin('primitives_breakpoint_l_min')} {
    display: none;
  }
`

const ResultsList = styled.div`
  > * {
    margin-bottom: ${space('primitives_space_16')};;
  }

  > *:last-child {
    margin-bottom: 0;
  }
`

export function Results() {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { productAdsBanner } = useOptimizelyFlagContext()
  const { resetOptionalFilters } = useFilters()
  const {
    isFetching,
    results,
    totalResults,
    error,
    filteredCollection,
    ga4FilterCollection,
    ga4Impressions,
    accommodationTips,
  } = useResults()
  const contextItemId = useCMSPageField('contextItemId')
  const { resultsPerPage, isFirstPage } = usePagination()
  const { roomsParticipantDistribution } = useRoomsParticipantsDistributionStore()

  // Indicates whether the search results are limited when the room participant distribution exceeds 3 people
  const [hasLimitedSearchResults, setHasLimitedSearchResults] = useState(false)
  // Indicates whether the search results are empty, because of an excessive amount of participants in a room
  const [hasExcesiveParticipants, setHasExcesiveParticipants] = useState(false)

  useEffect(() => {
    // Check if any room in the participant distribution has more than 3 people
    setHasLimitedSearchResults(
      roomsParticipantDistribution.some(room => room.adults + room.children.length + room.babies.length > 3),
    )
    setHasExcesiveParticipants(
      roomsParticipantDistribution.some(room => room.adults + room.children.length + room.babies.length >= 10),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Disabled because we only want to listen to changes in 'results' and not 'roomsParticipantDistribution'
  }, [results])

  useEffect(() => {
    sendEvent({
      event: 'eec.impressionView',
      ecommerce: {
        impressions: ga4Impressions,
      },
    })
  }, [ga4Impressions, sendEvent])

  useEffect(() => {
    sendEvent({
      event: 'filterCollection',
      ...ga4FilterCollection,
    })
  }, [ga4FilterCollection, sendEvent])

  const hasResults = totalResults !== 0
  const resultsElement = useRef<HTMLDivElement | null>(null)
  const renderLister = isFirstPage && totalResults >= resultsPerPage
  const renderAccommodationTips = accommodationTips.length > 0 && renderLister
  const renderProductAds = accommodationTips.length === 0 && renderLister
  const renderProductAdsBanner = productAdsBanner && renderLister

  const ACTIVATE_UDS_SEARCH_NEW_COLLECTIONBANNER = useFeatureFlag('ACTIVATE_UDS_SEARCH_NEW_COLLECTIONBANNER')
  const ACTIVATE_UDS_SEARCH_PRODUCT_CARD = useFeatureFlag('ACTIVATE_UDS_SEARCH_PRODUCT_CARD')

  return (
    <Wrapper ref={resultsElement}>
      <Header>
        { ACTIVATE_UDS_SEARCH_PRODUCT_CARD
          ? <ResultsCounter isLoading={isFetching} />
          : <ResultsCounterLegacy isLoading={isFetching} />}
        <Sorting />
      </Header>
      {error && (
        <EmptyState
          title={intl.formatMessage({
            id: 'search_results_error_title',
          })}
          description={intl.formatMessage({
            id: 'search_results_error_description',
          })}
          confirmationText={intl.formatMessage({
            id: 'search_results_error_action_label',
          })}
          onConfirmClick={resetOptionalFilters}
        />
      )}
      {!error && !hasResults && hasExcesiveParticipants && (
        <EmptyState
          title={intl.formatMessage({
            id: 'search_results_no_results_participants_title',
          })}
          description={intl.formatMessage({
            id: 'search_results_no_results_participants_description',
          })}
        />
      )}
      {!error && !hasResults && !hasExcesiveParticipants && (
        <EmptyState
          title={intl.formatMessage({
            id: 'search_results_no_results_title',
          })}
          description={intl.formatMessage({
            id: 'search_results_no_results_description',
          })}
          confirmationText={intl.formatMessage({
            id: 'search_results_no_results_action_label',
          })}
          onConfirmClick={resetOptionalFilters}
        />
      )}
      {hasResults && (
        <>
          {hasLimitedSearchResults && (
            <Banner color="warning" size="M" display="inline" supportingText={intl.formatMessage({ id: 'search_page_limited_amount_search_results' })} text="" />
          )}
          {filteredCollection && (
            ACTIVATE_UDS_SEARCH_NEW_COLLECTIONBANNER
              ? (
                  <CollectionBanner
                    collection={filteredCollection.collection}
                    description={filteredCollection.description}
                    image={filteredCollection.image}
                    title={filteredCollection.title}
                    usps={filteredCollection.usps}
                  />
                )
              : (
                  <CollectionBannerLegacy
                    collection={filteredCollection.collection}
                    description={filteredCollection.description}
                    image={filteredCollection.image}
                    title={filteredCollection.title}
                    usps={filteredCollection.usps}
                  />
                )
          )}
          <Content>
            {ACTIVATE_UDS_SEARCH_PRODUCT_CARD
              ? (
                  <MobileOnlyUDS>
                    <SelectedFiltersTokens withResetButton />
                  </MobileOnlyUDS>
                )
              : (
                  <MobileOnlyLegacy>
                    <SelectedFiltersTokens withResetButton />
                  </MobileOnlyLegacy>
                )}
            <ResultsList>
              {isFetching && range(0, resultsPerPage).map(index => <ProductCardLoading key={index} />)}
              {!isFetching
              && results.map((result, index) => (
                <Fragment key={result.deskId}>
                  <Result {...result} />
                  {renderAccommodationTips && index === 1 && (
                    <AccomodationTipsSlider items={accommodationTips} participants={roomsParticipantDistribution} />
                  )}
                  {renderProductAdsBanner && index === 3 && (
                    <ProductAdsBanner
                      compact
                      componentType="SearchBanner"
                    />
                  )}
                  {renderProductAds && index === 1 && (
                    <SearchTileProductAds
                      contextItemId={contextItemId}
                      headerTitle={intl.formatMessage({ id: 'search_result_product_ads_title' })}
                      infoLabel={intl.formatMessage({ id: 'search_result_product_ads_label' })}
                      infoContent={intl.formatMessage({ id: 'search_result_product_ads_content' })}
                    />
                  )}
                  {index % 6 === 1 && index < resultsPerPage - 2 && <Usps uspVisibleOnMobile={1} />}
                  {index % 6 === 3 && index < resultsPerPage - 2 && <Usps uspVisibleOnMobile={2} />}
                  {index % 6 === 5 && index < resultsPerPage - 2 && <Usps uspVisibleOnMobile={3} />}
                </Fragment>
              ))}
            </ResultsList>
          </Content>
          <Pagination scrollToElement={resultsElement} />
        </>
      )}
    </Wrapper>
  )
}
